import {
  CandidateRoleInterviewStatus,
  CandidateRoleScreeningStatus,
} from '@internal/api/roles/roles';
import MixpanelService from '@internal/services/mixpanel-service';
import { MEvents } from '@internal/utils/mixpanel';

import {
  CandidateRole,
  hasBookedInterview,
  isCallCompleted,
  isInterviewCleared,
  isTestCompleted,
} from '../roles';

export interface JourneyEventFlags {
  profileCompleted: boolean;
  testCompleted: boolean;
  markCallCompleted: boolean;
  recruiterCallAvailable: boolean;
  recruiterCallBooked: boolean;
  recruiterCallCompleted: boolean;
  recruiterCallSkipped: boolean;
  techInterviewAvailable: boolean;
  techInterviewBooked: boolean;
  techInterviewCompleted: boolean;
}

const FLAG_EVENTS: Record<keyof JourneyEventFlags, MEvents> = {
  profileCompleted: MEvents.ProfileCompleted,
  testCompleted: MEvents.TestCompleted,
  markCallCompleted: MEvents.MarkCallCompleted,
  recruiterCallAvailable: MEvents.RecruiterCallAvailable,
  recruiterCallBooked: MEvents.RecruiterCallBooked,
  recruiterCallCompleted: MEvents.RecruiterCallCompleted,
  recruiterCallSkipped: MEvents.RecruiterCallSkipped,
  techInterviewAvailable: MEvents.TechInterviewAvailable,
  techInterviewBooked: MEvents.TechInterviewBooked,
  techInterviewCompleted: MEvents.TechInterviewCompleted,
};

export const computeJourneyEventFlags = (
  roles: CandidateRole[],
  profileComplete: boolean
): JourneyEventFlags => {
  const screenings = roles.map(role => role.assessment.screening.status);

  return {
    profileCompleted: profileComplete,
    testCompleted: roles.some(isTestCompleted),
    markCallCompleted: roles.some(isCallCompleted),
    recruiterCallAvailable: screenings.some(
      status =>
        status !== CandidateRoleScreeningStatus.None &&
        status !== CandidateRoleScreeningStatus.Skipped
    ),
    recruiterCallBooked: screenings.some(
      status =>
        status === CandidateRoleScreeningStatus.Booked ||
        status === CandidateRoleScreeningStatus.Completed
    ),
    recruiterCallCompleted: screenings.includes(
      CandidateRoleScreeningStatus.Completed
    ),
    recruiterCallSkipped: screenings.includes(
      CandidateRoleScreeningStatus.Skipped
    ),
    techInterviewAvailable: roles.some(isInterviewCleared),
    techInterviewBooked: roles.some(hasBookedInterview),
    techInterviewCompleted: roles.some(
      role =>
        role.assessment.interview.status ===
        CandidateRoleInterviewStatus.Completed
    ),
  };
};

const seenFlagsByUser = new Map<string, JourneyEventFlags>();

const FIRED_STORAGE_KEY = 'nonGatedJourneyEvents';

type FiredEventsByUser = Record<string, string[]>;

const readFiredEvents = (apiKey: string): Set<string> => {
  try {
    const raw = localStorage.getItem(FIRED_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as FiredEventsByUser) : {};
    return new Set(parsed[apiKey] ?? []);
  } catch {
    return new Set();
  }
};

const saveFiredEvents = (apiKey: string, fired: Set<string>): void => {
  try {
    const raw = localStorage.getItem(FIRED_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as FiredEventsByUser) : {};
    parsed[apiKey] = [...fired];
    localStorage.setItem(FIRED_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Storage may be unavailable (private mode) — in-tab dedupe still applies.
  }
};

export const trackJourneyEvents = (
  apiKey: string,
  flags: JourneyEventFlags
): void => {
  const seen = seenFlagsByUser.get(apiKey);
  if (!seen) {
    seenFlagsByUser.set(apiKey, { ...flags });
    return;
  }

  const keys = Object.keys(FLAG_EVENTS) as Array<keyof JourneyEventFlags>;
  if (!keys.some(key => flags[key] && !seen[key])) return;

  const fired = readFiredEvents(apiKey);
  keys.forEach(key => {
    if (!flags[key] || seen[key]) return;
    seen[key] = true;
    const event = FLAG_EVENTS[key];
    if (fired.has(event)) return;
    fired.add(event);
    MixpanelService.addEvent(event);
  });
  saveFiredEvents(apiKey, fired);
};
